import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PropertyCard from "@/components/PropertyCard";
import { Filter, SlidersHorizontal, BookmarkPlus, X, Map as MapIcon, ChevronDown, ChevronUp, Flame, Snowflake, Armchair, WashingMachine, UtensilsCrossed, MoveVertical, DoorOpen, Trees, CircleParking, Loader2 } from "lucide-react";
import { persistLastSearch, computeMatchScore } from "@/lib/matchScore";
import { useSeo, TYPE_LABELS, breadcrumbLd } from "@/lib/seo";
import CityLinks from "@/components/CityLinks";

const BOOL_FEATURE_KEYS = ["air_conditioning", "furnished", "washing_machine", "dishwasher", "elevator", "balcony"];
const BOOL_FEATURE_FILTERS = [
    { k: "air_conditioning", l: "Aria condizionata", icon: Snowflake },
    { k: "furnished", l: "Arredamento", icon: Armchair },
    { k: "washing_machine", l: "Lavatrice", icon: WashingMachine },
    { k: "dishwasher", l: "Lavastoviglie", icon: UtensilsCrossed },
    { k: "elevator", l: "Ascensore", icon: MoveVertical },
    { k: "balcony", l: "Balcone/Terrazzo", icon: DoorOpen },
];
const ENUM_FEATURE_FILTERS = [
    { k: "heating", l: "Riscaldamento", icon: Flame, options: [{ v: "autonomo", l: "Autonomo" }, { v: "centralizzato", l: "Centralizzato" }] },
    { k: "garden", l: "Giardino", icon: Trees, options: [{ v: "privato", l: "Privato" }, { v: "condominiale", l: "Condominiale" }, { v: "no", l: "Nessuno" }] },
    { k: "parking", l: "Posto auto", icon: CircleParking, options: [{ v: "box", l: "Box" }, { v: "coperto", l: "Coperto" }, { v: "scoperto", l: "Scoperto" }, { v: "no", l: "Nessuno" }] },
];

export default function Listings() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const PAGE_SIZE = 24;
    const [filters, setFilters] = useState({
        city: searchParams.get("city") || "",
        property_type: searchParams.get("property_type") || "",
        transaction_type: searchParams.get("transaction_type") || "",
        contract_type: searchParams.get("contract_type") || "",
        condition: searchParams.get("condition") || "",
        owner_role: searchParams.get("owner_role") || "",
        min_price: "",
        max_price: "",
        min_rooms: "",
        sort: "recent",
        fresh: searchParams.get("fresh") === "1",
        heating: searchParams.get("heating") || "", garden: searchParams.get("garden") || "", parking: searchParams.get("parking") || "",
        air_conditioning: searchParams.get("air_conditioning") || "",
        furnished: searchParams.get("furnished") || "",
        washing_machine: searchParams.get("washing_machine") || "",
        dishwasher: searchParams.get("dishwasher") || "",
        elevator: searchParams.get("elevator") || "",
        balcony: searchParams.get("balcony") || "",
    });
    const [facets, setFacets] = useState({
        transaction_type: { all: 0, vendita: 0, affitto: 0 },
        owner_role: { all: 0, agenzia: 0, privato: 0 },
        fresh: { days: 7, count: 0 },
    });
    const [statsMap, setStatsMap] = useState({});
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [searchName, setSearchName] = useState("");
    const [saving, setSaving] = useState(false);
    const [showFeatureFilters, setShowFeatureFilters] = useState(false);
    const [cityOptions, setCityOptions] = useState([]);

    useEffect(() => {
        // Popola dinamicamente i suggerimenti città dagli annunci reali attivi (nessuna lista hardcoded)
        api.get("/properties/cities").then((r) => setCityOptions(r.data.cities || [])).catch(() => {});
    }, []);

    const fetchData = async (f, { loadMore = false } = {}) => {
        const nextPage = loadMore ? page + 1 : 1;
        if (loadMore) setLoadingMore(true); else setLoading(true);
        try {
            const params = { page: nextPage, limit: PAGE_SIZE };
            Object.entries(f).forEach(([k, v]) => {
                if (BOOL_FEATURE_KEYS.includes(k)) {
                    if (v === "yes") params[k] = true;
                    else if (v === "no") params[k] = false;
                    return;
                }
                if (v === "" || v == null || v === false) return;
                if (k === "fresh" && v === true) { params.fresh_days = 7; return; }
                if (k === "city" && typeof v === "string") {
                    const trimmed = v.trim();
                    if (!trimmed) return;
                    params[k] = trimmed;
                    return;
                }
                params[k] = v;
            });
            const requests = [api.get("/properties", { params })];
            if (!loadMore) requests.push(api.get("/properties/facets", { params: (() => { const p = { ...params }; delete p.fresh_days; delete p.page; delete p.limit; return p; })() }));
            const [propRes, facetRes] = await Promise.all(requests);
            const newItems = propRes.data.items || [];
            setItems((prev) => {
                let merged = loadMore ? [...prev, ...newItems] : newItems;
                if (f.sort === "compatibility") {
                    const prefs = {};
                    if (f.city) prefs.pref_city = f.city;
                    if (f.max_price) prefs.pref_max_price = Number(f.max_price);
                    if (f.min_rooms) prefs.pref_min_rooms = Number(f.min_rooms);
                    merged = [...merged].sort((a, b) => (computeMatchScore(prefs, b) ?? -1) - (computeMatchScore(prefs, a) ?? -1));
                }
                return merged;
            });
            setPage(nextPage);
            setTotalCount(propRes.data.total_count ?? newItems.length);
            if (!loadMore && facetRes?.data) setFacets(facetRes.data);
            // Batch fetch stats per far apparire il fire counter sulle card (best-effort)
            const ids = newItems.map((p) => p.id).filter(Boolean);
            if (ids.length > 0) {
                api.post("/properties/stats-batch", { ids })
                    .then((r) => setStatsMap((prevStats) => ({ ...prevStats, ...(r?.data?.stats || {}) })))
                    .catch(() => {});
            }
            if (!loadMore) {
                // Persist last search (guest = localStorage, user = backend) — usato dal Match Score
                persistLastSearch(f);
                if (user) {
                    const prefs = {};
                    if (f.city) prefs.pref_city = f.city;
                    if (f.max_price) prefs.pref_max_price = Number(f.max_price);
                    if (f.min_rooms) prefs.pref_min_rooms = Number(f.min_rooms);
                    if (Object.keys(prefs).length > 0) {
                        api.put("/auth/profile", { preferences: prefs }).catch(() => {});
                    }
                }
            }
        } finally {
            if (loadMore) setLoadingMore(false); else setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(filters); }, []);

    const seoCity = searchParams.get("city") || "";
    const seoType = TYPE_LABELS[searchParams.get("property_type")] || "";
    const seoOp = searchParams.get("transaction_type") === "affitto" ? "in affitto" : searchParams.get("transaction_type") === "vendita" ? "in vendita" : "in vendita e affitto";
    const seoSubject = seoType ? `${seoType === "Appartamento" ? "Appartamenti" : seoType}` : "Case";
    useSeo({
        title: `${seoSubject} ${seoOp}${seoCity ? ` a ${seoCity}` : " in Italia"} | VendiDirect`,
        description: `Annunci immobiliari ${seoOp}${seoCity ? ` a ${seoCity}` : " in tutta Italia"}: ${seoSubject.toLowerCase()} con foto, prezzo e contatto diretto con il proprietario o l'agenzia. Filtra per prezzo, locali e servizi.`,
        canonical: "/annunci",
        jsonLd: breadcrumbLd([{ name: "Home", path: "/" }, { name: "Annunci", path: "/annunci" }]),
    }, [seoCity, seoType, seoOp]);

    const apply = () => {
        const sp = {};
        if (filters.city) sp.city = filters.city;
        if (filters.property_type) sp.property_type = filters.property_type;
        if (filters.transaction_type) sp.transaction_type = filters.transaction_type;
        if (filters.contract_type) sp.contract_type = filters.contract_type;
        if (filters.condition) sp.condition = filters.condition;
        if (filters.owner_role) sp.owner_role = filters.owner_role;
        if (filters.fresh) sp.fresh = "1";
        if (filters.heating) sp.heating = filters.heating;
        if (filters.garden) sp.garden = filters.garden;
        if (filters.parking) sp.parking = filters.parking;
        BOOL_FEATURE_KEYS.forEach((k) => { if (filters[k]) sp[k] = filters[k]; });
        setSearchParams(sp);
        fetchData(filters);
    };

    const syncUrl = (nf) => {
        const sp = {};
        if (nf.city) sp.city = nf.city;
        if (nf.property_type) sp.property_type = nf.property_type;
        if (nf.transaction_type) sp.transaction_type = nf.transaction_type;
        if (nf.contract_type) sp.contract_type = nf.contract_type;
        if (nf.condition) sp.condition = nf.condition;
        if (nf.owner_role) sp.owner_role = nf.owner_role;
        if (nf.fresh) sp.fresh = "1";
        if (nf.heating) sp.heating = nf.heating;
        if (nf.garden) sp.garden = nf.garden;
        if (nf.parking) sp.parking = nf.parking;
        BOOL_FEATURE_KEYS.forEach((k) => { if (nf[k]) sp[k] = nf[k]; });
        setSearchParams(sp);
    };

    return (
        <div data-testid="listings-page" className="pt-32 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28">
            <div className="mb-12">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-eyebrow">Catalogo</span>
                        <h1 className="font-serif text-4xl sm:text-5xl text-ink-50 mt-3">Annunci immobiliari</h1>
                    </div>
                    <button
                        data-testid="view-map-catalog"
                        onClick={() => {
                            const sp = new URLSearchParams();
                            Object.entries(filters).forEach(([k, v]) => { if (v && k !== "sort" && k !== "fresh") sp.set(k, v); });
                            navigate(`/annunci/mappa${sp.toString() ? "?" + sp.toString() : ""}`);
                        }}
                        className="mt-2 flex items-center gap-2 px-4 py-2.5 border border-gold/50 text-gold text-xs uppercase tracking-widest hover:bg-gold hover:text-black transition-colors"
                    >
                        <MapIcon size={14} /> Vista Mappa
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Filters */}
                <aside className="lg:col-span-3">
                    <div className="border border-white/10 p-6 sticky top-28">
                        <div className="flex items-center gap-2 mb-6">
                            <SlidersHorizontal size={14} className="text-gold" />
                            <span className="label-eyebrow">Filtri</span>
                        </div>
                        <div className="space-y-5 text-sm">
                            <div>
                                <label className="block text-ink-400 text-xs mb-2">Tipo</label>
                                <div className="grid grid-cols-3 gap-1 border border-white/10 p-1" data-testid="filter-transaction-group">
                                    {[
                                        { v: "", l: "Tutti", c: facets.transaction_type.all },
                                        { v: "vendita", l: "Vendita", c: facets.transaction_type.vendita },
                                        { v: "affitto", l: "Affitto", c: facets.transaction_type.affitto },
                                    ].map((opt) => (
                                        <button
                                            key={opt.v || "all"}
                                            data-testid={`filter-transaction-${opt.v || "all"}`}
                                            onClick={() => { const nf = { ...filters, transaction_type: opt.v, contract_type: opt.v === "affitto" ? filters.contract_type : "" }; setFilters(nf); fetchData(nf); syncUrl(nf); }}
                                            className={`py-2 text-xs uppercase tracking-wider transition-colors ${filters.transaction_type === opt.v ? "bg-gold text-black" : "text-ink-400 hover:text-ink-100"}`}
                                        >
                                            {opt.l} <span className="opacity-70 normal-case">({opt.c})</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-ink-400 text-xs mb-2">Inserzionista</label>
                                <div className="grid grid-cols-3 gap-1 border border-white/10 p-1" data-testid="filter-owner-role-group">
                                    {[
                                        { v: "", l: "Tutti", c: facets.owner_role.all },
                                        { v: "agenzia", l: "Agenzia", c: facets.owner_role.agenzia },
                                        { v: "privato", l: "Privato", c: facets.owner_role.privato },
                                    ].map((opt) => (
                                        <button
                                            key={opt.v || "all"}
                                            data-testid={`filter-owner-role-${opt.v || "all"}`}
                                            onClick={() => { const nf = { ...filters, owner_role: opt.v }; setFilters(nf); fetchData(nf); syncUrl(nf); }}
                                            className={`py-2 text-xs uppercase tracking-wider transition-colors ${filters.owner_role === opt.v ? "bg-gold text-black" : "text-ink-400 hover:text-ink-100"}`}
                                        >
                                            {opt.l} <span className="opacity-70 normal-case">({opt.c})</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-ink-400 text-xs mb-2">Città</label>
                                <input data-testid="filter-city" list="city-suggestions" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-ink-50 focus:border-gold outline-none" placeholder="Milano..." />
                                <datalist id="city-suggestions" data-testid="city-suggestions">
                                    {cityOptions.map((c) => <option key={c} value={c} />)}
                                </datalist>
                            </div>
                            <div>
                                <label className="block text-ink-400 text-xs mb-2">Tipologia</label>
                                <select data-testid="filter-type" value={filters.property_type} onChange={(e) => setFilters({ ...filters, property_type: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-ink-50 focus:border-gold outline-none">
                                    <option value="" className="bg-ink-950">Tutte</option>
                                    <option value="appartamento" className="bg-ink-950">Appartamento</option>
                                    <option value="villa" className="bg-ink-950">Villa</option>
                                    <option value="attico" className="bg-ink-950">Attico</option>
                                    <option value="loft" className="bg-ink-950">Loft</option>
                                    <option value="casa" className="bg-ink-950">Casa</option>
                                    <option value="cantina" className="bg-ink-950">Cantina</option>
                                    <option value="uffici" className="bg-ink-950">Uffici</option>
                                    <option value="locali_commerciali" className="bg-ink-950">Locali commerciali</option>
                                    <option value="box_auto" className="bg-ink-950">Box auto</option>
                                </select>
                            </div>
                            {filters.transaction_type === "affitto" && (
                                <div>
                                    <label className="block text-ink-400 text-xs mb-2">Tipologia contratto</label>
                                    <select data-testid="filter-contract-type" value={filters.contract_type} onChange={(e) => setFilters({ ...filters, contract_type: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-ink-50 focus:border-gold outline-none">
                                        <option value="" className="bg-ink-950">Tutti</option>
                                        <optgroup label="Residenziale" className="bg-ink-950">
                                            <option value="residenziale" className="bg-ink-950">Residenziale</option>
                                            <option value="transitorio" className="bg-ink-950">Transitorio</option>
                                            <option value="lungo_termine" className="bg-ink-950">Lungo termine</option>
                                        </optgroup>
                                        <optgroup label="Affitti brevi e vacanze" className="bg-ink-950">
                                            <option value="casa_vacanze" className="bg-ink-950">Casa vacanze</option>
                                            <option value="bb" className="bg-ink-950">B&amp;B</option>
                                            <option value="affitto_breve" className="bg-ink-950">Affitto breve</option>
                                        </optgroup>
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="block text-ink-400 text-xs mb-2">Stato dell&apos;immobile</label>
                                <select data-testid="filter-condition" value={filters.condition} onChange={(e) => setFilters({ ...filters, condition: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-ink-50 focus:border-gold outline-none">
                                    <option value="" className="bg-ink-950">Tutti</option>
                                    <option value="nuovo" className="bg-ink-950">Nuovo</option>
                                    <option value="ristrutturato" className="bg-ink-950">Ristrutturato</option>
                                    <option value="abitabile" className="bg-ink-950">Abitabile</option>
                                    <option value="da_ristrutturare" className="bg-ink-950">Da ristrutturare</option>
                                </select>
                            </div>

                            {/* Caratteristiche e Servizi — collassabile, stile neumorphic 3D */}
                            <div className="feature-card-neu p-4">
                                <button
                                    type="button"
                                    data-testid="filter-features-toggle"
                                    onClick={() => setShowFeatureFilters((v) => !v)}
                                    className="w-full flex items-center justify-between text-ink-400 text-xs uppercase tracking-wider"
                                >
                                    Caratteristiche e Servizi
                                    {showFeatureFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                                {showFeatureFilters && (
                                    <div className="mt-4 space-y-4">
                                        {ENUM_FEATURE_FILTERS.map((f) => (
                                            <div key={f.k}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="feature-icon-badge"><f.icon size={13} /></span>
                                                    <label className="text-ink-400 text-xs">{f.l}</label>
                                                </div>
                                                <select
                                                    data-testid={`filter-${f.k}`}
                                                    value={filters[f.k]}
                                                    onChange={(e) => setFilters({ ...filters, [f.k]: e.target.value })}
                                                    className="feature-select-neu w-full px-3 py-2 text-ink-50 outline-none text-sm"
                                                >
                                                    <option value="" className="bg-ink-950">Tutti</option>
                                                    {f.options.map((o) => <option key={o.v} value={o.v} className="bg-ink-950">{o.l}</option>)}
                                                </select>
                                            </div>
                                        ))}
                                        {BOOL_FEATURE_FILTERS.map((f) => (
                                            <div key={f.k}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="feature-icon-badge"><f.icon size={13} /></span>
                                                    <label className="text-ink-400 text-xs">{f.l}</label>
                                                </div>
                                                <div className="flex gap-2">
                                                    {[{ v: "", l: "Tutti" }, { v: "yes", l: "Sì" }, { v: "no", l: "No" }].map((opt) => (
                                                        <button
                                                            key={opt.v || "any"}
                                                            type="button"
                                                            data-testid={`filter-${f.k}-${opt.v || "any"}`}
                                                            onClick={() => setFilters({ ...filters, [f.k]: opt.v })}
                                                            className={`feature-toggle-btn ${filters[f.k] === opt.v ? "is-active" : ""}`}
                                                        >
                                                            {opt.l}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-ink-400 text-xs mb-2">Prezzo min</label>
                                    <input data-testid="filter-min-price" type="number" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-ink-50 focus:border-gold outline-none" />
                                </div>
                                <div>
                                    <label className="block text-ink-400 text-xs mb-2">Prezzo max</label>
                                    <input data-testid="filter-max-price" type="number" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-ink-50 focus:border-gold outline-none" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-ink-400 text-xs mb-2">Locali min</label>
                                <input data-testid="filter-min-rooms" type="number" value={filters.min_rooms} onChange={(e) => setFilters({ ...filters, min_rooms: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-ink-50 focus:border-gold outline-none" />
                            </div>
                            <div>
                                <label className="block text-ink-400 text-xs mb-2">Ordina per</label>
                                <div className="flex gap-2">
                                    <select data-testid="filter-sort" value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })} className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-ink-50 focus:border-gold outline-none">
                                        <option value="recent" className="bg-ink-950">Più recenti</option>
                                        <option value="price_asc" className="bg-ink-950">Prezzo crescente</option>
                                        <option value="price_desc" className="bg-ink-950">Prezzo decrescente</option>
                                        <option value="featured" className="bg-ink-950">In evidenza</option>
                                        <option value="compatibility" className="bg-ink-950">Per compatibilità</option>
                                    </select>
                                    <button
                                        data-testid="filter-fresh"
                                        type="button"
                                        onClick={() => { const nf = { ...filters, fresh: !filters.fresh }; setFilters(nf); fetchData(nf); syncUrl(nf); }}
                                        title={`Solo annunci pubblicati negli ultimi ${facets.fresh.days || 7} giorni`}
                                        className={`px-3 py-2 border text-xs uppercase tracking-wider transition-colors whitespace-nowrap ${filters.fresh ? "bg-gold text-black border-gold" : "border-white/10 text-ink-400 hover:text-ink-100 hover:border-gold/50"}`}
                                    >
                                        Novità <span className="opacity-70 normal-case">({facets.fresh.count})</span>
                                    </button>
                                </div>
                            </div>
                            <button data-testid="apply-filters" onClick={apply} className="w-full bg-gold text-black py-3 text-sm font-medium hover:bg-gold-soft transition-colors flex items-center justify-center gap-2">
                                <Filter size={14} /> Applica filtri
                            </button>
                            <button
                                data-testid="save-search-button"
                                onClick={() => { if (!user) { navigate("/login"); return; } setSearchName(filters.city ? `Ricerca ${filters.city}` : "La mia ricerca"); setShowSaveModal(true); }}
                                className="w-full border border-white/15 text-ink-100 py-3 text-sm hover:border-gold hover:text-gold transition-colors flex items-center justify-center gap-2 mt-2"
                            >
                                <BookmarkPlus size={14} /> Salva ricerca
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Results */}
                <main className="lg:col-span-9">
                    <div className="text-sm text-ink-400 mb-6" data-testid="results-count">
                        {loading ? "Caricamento..." : `${totalCount} risultat${totalCount === 1 ? "o" : "i"}`}
                    </div>
                    {!loading && items.length === 0 && (
                        <div className="text-ink-400 py-24 text-center border border-white/10">Nessun risultato. Prova a modificare i filtri.</div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                        {items.map((p, i) => <PropertyCard key={p.id} property={p} index={i} stats={statsMap[p.id]} />)}
                    </div>
                    {!loading && items.length < totalCount && (
                        <div className="mt-10 flex justify-center">
                            <button
                                type="button"
                                data-testid="load-more-button"
                                disabled={loadingMore}
                                onClick={() => fetchData(filters, { loadMore: true })}
                                className="inline-flex items-center gap-2 px-8 py-3.5 border border-gold/50 text-gold hover:bg-gold hover:text-black transition-colors text-sm disabled:opacity-50"
                            >
                                {loadingMore ? <Loader2 size={14} className="animate-spin" /> : null}
                                {loadingMore ? "Caricamento..." : "Carica altri"}
                            </button>
                        </div>
                    )}
                    <div className="mt-20" data-testid="listings-cities">
                        <span className="label-eyebrow">Esplora per città</span>
                        <div className="mt-5"><CityLinks limit={12} /></div>
                    </div>
                </main>
            </div>

            {showSaveModal && (
                <div data-testid="save-search-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShowSaveModal(false)}>
                    <div className="bg-ink-950 border border-gold/40 p-10 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <span className="label-eyebrow">Salva ricerca</span>
                            <button onClick={() => setShowSaveModal(false)} className="text-ink-400 hover:text-gold"><X size={18} /></button>
                        </div>
                        <h3 className="font-serif text-2xl text-ink-50 mb-4">Ricevi alert via email</h3>
                        <p className="text-sm text-ink-400 mb-6">Ti notificheremo via email quando esce un nuovo annuncio che corrisponde ai tuoi filtri.</p>
                        <input
                            data-testid="save-search-name"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            placeholder="Nome della ricerca"
                            className="w-full bg-transparent border border-white/15 px-4 py-3 text-ink-50 focus:border-gold outline-none mb-4"
                        />
                        <button
                            data-testid="save-search-confirm"
                            disabled={saving || !searchName}
                            onClick={async () => {
                                setSaving(true);
                                try {
                                    const payload = { name: searchName };
                                    Object.entries(filters).forEach(([k, v]) => {
                                        if (k !== "sort" && v !== "" && v != null) {
                                            payload[k] = ["min_price", "max_price"].includes(k) ? parseFloat(v) : ["min_rooms"].includes(k) ? parseInt(v) : v;
                                        }
                                    });
                                    await api.post("/saved-searches", payload);
                                    setShowSaveModal(false);
                                    navigate("/preferiti");
                                } finally {
                                    setSaving(false);
                                }
                            }}
                            className="w-full bg-gold text-black py-3 text-sm font-medium hover:bg-gold-soft transition-colors disabled:opacity-50"
                        >
                            {saving ? "Salvataggio..." : "Salva e attiva alert"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
